// Generalized realtime channel subscription hook.
//
// Subscribes a component to one `org:<orgId>:<topic>` Supabase channel and
// fans the messages out two ways:
//   1. `onEvent` callback — invoked for both Postgres-replication events on
//      `domain_events` (filtered by org) AND ad-hoc broadcast messages.
//   2. `invalidateKeys` — TanStack Query keys to invalidate whenever the
//      channel sees activity. This is the pattern most callers want; the
//      hook re-renders the dependent query, the SPA refetches.
//
// The hook is a no-op while `orgId` is empty so it's safe to mount before the
// org context resolves (common during the initial route render).

import { type QueryKey, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useRef } from 'react'
import { supabase } from '../supabase'
import { type RealtimeEvent, type RealtimeTopic, isPersistedRealtimeEvent } from './events'

export interface ChannelOptions {
  /** Tenant org id. The hook no-ops until this is non-empty. */
  orgId: string | null | undefined
  /** Topic suffix; channel name will be `org:<orgId>:<topic>`. */
  topic: RealtimeTopic
  /**
   * Called for every received event (both `postgres_changes` rows from
   * `domain_events` and `broadcast` payloads). Use `isPersistedRealtimeEvent`
   * to narrow the union if you need the persisted shape.
   */
  onEvent?: (event: RealtimeEvent) => void
  /**
   * TanStack Query keys to invalidate on every received event. The hook calls
   * `queryClient.invalidateQueries({ queryKey })` for each entry — exact-match
   * semantics, same as TanStack's default.
   */
  invalidateKeys?: QueryKey[]
}

/**
 * Subscribe the calling component to an org-scoped realtime topic.
 *
 * @example
 *   useRealtimeChannel({
 *     orgId,
 *     topic: 'takes',
 *     invalidateKeys: [['producer', 'project', projectId]],
 *     onEvent: (e) => { if (e.event_type === 'TakeApproved') toast(e) },
 *   })
 */
export function useRealtimeChannel(opts: ChannelOptions): void {
  const qc = useQueryClient()
  // Per-instance suffix so multiple subscribers on the same logical topic don't
  // collide on a shared Supabase channel. Supabase v2 keys channels by name,
  // and once any sibling has called `.subscribe()` further `.on(...)` calls on
  // a same-named channel throw "Cannot add ... callbacks ... after subscribe()".
  // A unique suffix gives every hook instance its own underlying channel.
  const instanceId = useId()

  // Latch the latest callback in a ref so we don't have to tear down the
  // subscription every time the parent re-renders with a new arrow fn.
  const handlerRef = useRef(opts.onEvent)
  handlerRef.current = opts.onEvent

  // Snapshot the keys once per change. Stringifying for the dep array keeps
  // referential changes from re-subscribing on every render.
  const keysRef = useRef(opts.invalidateKeys)
  keysRef.current = opts.invalidateKeys

  const orgId = opts.orgId ?? null
  const topic = opts.topic

  useEffect(() => {
    if (!orgId) return

    const channelName = `org:${orgId}:${topic}#${instanceId}`
    // Register all `.on(...)` callbacks BEFORE `.subscribe()` — Supabase v2
    // forbids adding callbacks afterwards.
    const channel = supabase.channel(channelName)
    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'domain_events',
        filter: `organization_id=eq.${orgId}`,
      },
      (payload) => {
        const row = (payload as { new?: unknown })?.new
        if (isPersistedRealtimeEvent(row)) {
          handlerRef.current?.(row)
        }
        for (const key of keysRef.current ?? []) {
          void qc.invalidateQueries({ queryKey: key })
        }
      },
    )
    channel.on('broadcast', { event: '*' }, (msg) => {
      const inner = (msg as { payload?: unknown })?.payload
      if (isPersistedRealtimeEvent(inner)) {
        handlerRef.current?.(inner)
      }
      for (const key of keysRef.current ?? []) {
        void qc.invalidateQueries({ queryKey: key })
      }
    })
    channel.subscribe()

    return () => {
      void supabase.removeChannel(channel)
    }
  }, [orgId, topic, qc, instanceId])
}
